import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  App,
  Avatar,
  Button,
  Card,
  Col,
  Divider,
  Empty,
  Form,
  Grid,
  Input,
  InputNumber,
  List,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography
} from 'antd'
import {
  AppstoreOutlined,
  BarcodeOutlined,
  BoxPlotOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  SettingOutlined,
  StockOutlined,
  TagsOutlined,
  UnorderedListOutlined
} from '@ant-design/icons'
import productService from '../../services/productService'
import inventoryService from '../../services/inventoryService'
import { getProductRecipe, saveProductRecipe } from '../../services/materialService'
import './ProductVariantManager.css'

const { Text, Title } = Typography
const { useBreakpoint } = Grid

const money = value => Number(value || 0).toLocaleString('th-TH', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})

const parseJson = (value, fallback) => {
  if (!value) return fallback
  if (typeof value === 'object') return value
  try {
    return JSON.parse(value)
  } catch {
    return fallback
  }
}

const ProductVariantManager = ({ onChanged }) => {
  const { message } = App.useApp()
  const screens = useBreakpoint()
  const isMobile = !screens.md
  const [families, setFamilies] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [variants, setVariants] = useState([])
  const [loading, setLoading] = useState(false)
  const [familySearch, setFamilySearch] = useState('')
  const [workspace, setWorkspace] = useState(null)
  const [detail, setDetail] = useState(null)
  const [detailData, setDetailData] = useState({ lots: [], serials: [], movements: [] })
  const [componentProducts, setComponentProducts] = useState([])
  const [familyForm] = Form.useForm()
  const [variantForm] = Form.useForm()
  const [unitForm] = Form.useForm()
  const [recipeForm] = Form.useForm()
  const [skuForm] = Form.useForm()

  const selected = useMemo(
    () => families.find(item => Number(item.id) === Number(selectedId)),
    [families, selectedId]
  )

  const filteredFamilies = useMemo(() => {
    const keyword = familySearch.trim().toLowerCase()
    if (!keyword) return families
    return families.filter(item => `${item.name || ''} ${item.brand || ''}`.toLowerCase().includes(keyword))
  }, [families, familySearch])

  const loadFamilies = useCallback(async () => {
    setLoading(true)
    try {
      const rows = await productService.getFamilies()
      setFamilies(rows)
      setSelectedId(current => current || rows[0]?.id || null)
    } catch (error) {
      message.error(error.response?.data?.message || 'โหลดสินค้าแม่ไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [message])

  const loadVariants = useCallback(async () => {
    if (!selectedId) {
      setVariants([])
      return
    }
    setLoading(true)
    try {
      setVariants(await productService.getFamilyVariants(selectedId))
    } catch (error) {
      message.error(error.response?.data?.message || 'โหลด Variant ไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [selectedId, message])

  useEffect(() => {
    loadFamilies()
  }, [loadFamilies])

  useEffect(() => {
    loadVariants()
  }, [loadVariants])

  const openFamily = () => {
    familyForm.resetFields()
    familyForm.setFieldsValue({
      attribute_schema: [{ key: 'size', label: 'ขนาด' }],
      default_shop_warranty_months: 0,
      default_manufacturer_warranty_months: 0
    })
    setWorkspace('family')
  }

  const saveFamily = async () => {
    try {
      await productService.createFamily(await familyForm.validateFields())
      message.success('สร้างสินค้าแม่แล้ว')
      setWorkspace(null)
      await loadFamilies()
      onChanged?.()
    } catch (error) {
      if (!error.errorFields) message.error(error.response?.data?.message || 'บันทึกสินค้าแม่ไม่สำเร็จ')
    }
  }

  const openVariant = () => {
    if (!selected) {
      message.warning('กรุณาเลือกสินค้าแม่ก่อนเพิ่ม Variant')
      return
    }
    const attributes = {}
    parseJson(selected.attribute_schema, []).forEach(item => { attributes[item.key] = '' })
    variantForm.resetFields()
    variantForm.setFieldsValue({
      variant_attributes: attributes,
      track_inventory: true,
      track_serial: false,
      shop_warranty_months: selected.default_shop_warranty_months || 0,
      manufacturer_warranty_months: selected.default_manufacturer_warranty_months || 0,
      units: [{
        unit_name: 'ชิ้น',
        conversion_to_base: 1,
        is_base: true,
        is_default_sale: true,
        is_default_purchase: true
      }]
    })
    setWorkspace('variant')
  }

  const saveVariant = async () => {
    try {
      const values = await variantForm.validateFields()
      await productService.createVariant(selectedId, values)
      message.success('สร้าง SKU / Variant แล้ว')
      setWorkspace(null)
      await loadVariants()
      onChanged?.()
    } catch (error) {
      if (!error.errorFields) message.error(error.response?.data?.message || 'บันทึก Variant ไม่สำเร็จ')
    }
  }

  const openDetail = async row => {
    setDetail(row)
    setWorkspace('detail')
    unitForm.setFieldsValue({
      units: (row.units || []).map(item => ({
        ...item,
        is_base: Boolean(item.is_base),
        is_default_sale: Boolean(item.is_default_sale),
        is_default_purchase: Boolean(item.is_default_purchase)
      }))
    })
    skuForm.setFieldsValue({
      ...row,
      track_inventory: Boolean(row.track_inventory),
      track_serial: Boolean(row.track_serial)
    })
    try {
      const [lots, serials, movements, recipeResponse, products] = await Promise.all([
        inventoryService.lots(row.id),
        inventoryService.serials(row.id),
        inventoryService.movements(row.id),
        getProductRecipe(row.id),
        productService.getAllProducts()
      ])
      setDetailData({ lots, serials, movements })
      setComponentProducts(products)
      const recipe = recipeResponse?.data?.data
      recipeForm.setFieldsValue({
        notes: recipe?.notes || '',
        components: (recipe?.components || []).map(component => ({
          ...component,
          material_selection: component.material_product_id
            ? `${component.material_product_id}:${component.material_product_unit_id || ''}`
            : undefined,
          track_stock: Boolean(component.track_stock),
          allow_rotation: component.allow_rotation !== false
        }))
      })
    } catch {
      setDetailData({ lots: [], serials: [], movements: [] })
      setComponentProducts([])
      recipeForm.setFieldsValue({ notes: '', components: [] })
    }
  }

  const closeWorkspace = () => {
    setWorkspace(null)
    setDetail(null)
  }

  const saveUnits = async () => {
    try {
      const { units } = await unitForm.validateFields()
      await productService.updateProductUnits(detail.id, units)
      message.success('บันทึกหน่วยและราคาแล้ว')
      await loadVariants()
      onChanged?.()
    } catch (error) {
      if (!error.errorFields) message.error(error.response?.data?.message || 'บันทึกหน่วยไม่สำเร็จ')
    }
  }

  const saveSku = async () => {
    try {
      const values = await skuForm.validateFields()
      await productService.updateProduct(detail.id, values)
      message.success('บันทึกข้อมูล SKU และการรับประกันแล้ว')
      setDetail(current => ({ ...current, ...values }))
      await loadVariants()
      onChanged?.()
    } catch (error) {
      if (!error.errorFields) message.error(error.response?.data?.message || 'บันทึกข้อมูล SKU ไม่สำเร็จ')
    }
  }

  const saveRecipe = async () => {
    try {
      const values = await recipeForm.validateFields()
      const components = (values.components || []).map(component => {
        const [productId, unitId] = String(component.material_selection || ':').split(':')
        const result = {
          ...component,
          material_product_id: productId ? Number(productId) : null,
          material_product_unit_id: unitId ? Number(unitId) : null
        }
        delete result.material_selection
        return result
      })
      await saveProductRecipe(detail.id, { notes: values.notes, components })
      message.success('บันทึก BOM revision ใหม่แล้ว')
    } catch (error) {
      if (!error.errorFields) message.error(error.response?.data?.message || 'บันทึก BOM ไม่สำเร็จ')
    }
  }

  const componentOptions = componentProducts.flatMap(product => {
    const units = (product.units || []).filter(unit => unit.is_active !== false)
    if (!units.length) {
      return [{ value: `${product.id}:`, label: `${product.name} · ${product.unit || 'หน่วยฐาน'}` }]
    }
    return units.map(unit => ({
      value: `${product.id}:${unit.id}`,
      label: `${product.name} · ${unit.unit_name} (×${Number(unit.conversion_to_base)})`
    }))
  })

  const renderAttributes = value => {
    const entries = Object.entries(parseJson(value, {}))
    return entries.length
      ? <Space size={[4, 4]} wrap>{entries.map(([key, item]) => <Tag key={key}>{key}: {item}</Tag>)}</Space>
      : <Text type="secondary">ไม่ระบุ</Text>
  }

  const columns = [
    {
      title: 'สินค้า / SKU',
      key: 'product',
      render: (_, row) => (
        <Space>
          <Avatar shape="square" icon={<BarcodeOutlined />} className="pvm-sku-avatar" />
          <div><Text strong>{row.name}</Text><br /><Text type="secondary">{row.barcode || '-'}</Text></div>
        </Space>
      )
    },
    { title: 'คุณลักษณะ', dataIndex: 'variant_attributes', render: renderAttributes },
    {
      title: 'หน่วยขาย',
      render: (_, row) => (
        <Space size={[4, 4]} wrap>
          {(row.units || []).map(unit => (
            <Tag color={unit.is_base ? 'blue' : 'default'} key={unit.id || unit.unit_name}>
              {unit.unit_name} × {Number(unit.conversion_to_base)}
            </Tag>
          ))}
        </Space>
      )
    },
    {
      title: 'คงเหลือ',
      dataIndex: 'stock',
      align: 'right',
      render: (value, row) => (
        <Text type={Number(value) <= Number(row.min_stock) ? 'danger' : undefined} strong>
          {Number(value || 0).toLocaleString()} {row.unit}
        </Text>
      )
    },
    {
      title: 'ราคาขาย',
      dataIndex: 'price',
      align: 'right',
      render: value => `${money(value)} บาท`
    },
    {
      title: '',
      width: 110,
      fixed: 'right',
      render: (_, row) => <Button icon={<SettingOutlined />} onClick={() => openDetail(row)}>จัดการ</Button>
    }
  ]

  const familyEditor = (
    <Form form={familyForm} layout="vertical" className="pvm-form">
      <div className="pvm-section-heading">
        <TagsOutlined />
        <div><Text strong>ข้อมูลสินค้าแม่</Text><Text type="secondary">ใช้รวม SKU ที่เป็นสินค้าเดียวกันแต่ต่างขนาด สี หรือรุ่น</Text></div>
      </div>
      <Row gutter={16}>
        <Col xs={24} md={12}><Form.Item name="name" label="ชื่อสินค้าแม่" rules={[{ required: true, message: 'กรุณาระบุชื่อสินค้าแม่' }]}><Input size="large" placeholder="เช่น สติ๊กเกอร์ PVC" /></Form.Item></Col>
        <Col xs={24} md={12}><Form.Item name="brand" label="แบรนด์"><Input size="large" placeholder="ระบุแบรนด์ (ถ้ามี)" /></Form.Item></Col>
      </Row>
      <Form.Item name="description" label="รายละเอียด"><Input.TextArea rows={3} placeholder="รายละเอียดภาพรวมของสินค้า" /></Form.Item>
      <Divider />
      <div className="pvm-section-heading">
        <AppstoreOutlined />
        <div><Text strong>คุณลักษณะที่ทำให้แต่ละ Variant แตกต่างกัน</Text><Text type="secondary">ตัวอย่าง: ขนาด, สี, ความกว้าง, รุ่น</Text></div>
      </div>
      <Form.List name="attribute_schema">{(fields, { add, remove }) => <>
        {fields.map((field, index) => (
          <Row gutter={10} key={field.key} align="middle" className="pvm-attribute-row">
            <Col xs={10} md={9}><Form.Item {...field} name={[field.name, 'key']} label={index === 0 ? 'รหัสคุณลักษณะ' : ''} rules={[{ required: true }]}><Input placeholder="เช่น size" /></Form.Item></Col>
            <Col xs={11} md={12}><Form.Item {...field} name={[field.name, 'label']} label={index === 0 ? 'ชื่อที่แสดง' : ''} rules={[{ required: true }]}><Input placeholder="เช่น ขนาด" /></Form.Item></Col>
            <Col xs={3}><Form.Item label={index === 0 ? ' ' : ''}><Button danger type="text" onClick={() => remove(field.name)}>ลบ</Button></Form.Item></Col>
          </Row>
        ))}
        <Button dashed icon={<PlusOutlined />} onClick={() => add()} block>เพิ่มคุณลักษณะ</Button>
      </>}</Form.List>
      <Divider />
      <Row gutter={16}>
        <Col xs={12}><Form.Item name="default_shop_warranty_months" label="ประกันร้านเริ่มต้น (เดือน)"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
        <Col xs={12}><Form.Item name="default_manufacturer_warranty_months" label="ประกันผู้ผลิตเริ่มต้น (เดือน)"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
      </Row>
    </Form>
  )

  const variantBasicEditor = (
    <Form form={variantForm} layout="vertical" className="pvm-form">
      <div className="pvm-parent-banner">
        <Avatar icon={<AppstoreOutlined />} />
        <div><Text type="secondary">กำลังเพิ่ม Variant ให้</Text><Text strong>{selected?.name}</Text></div>
      </div>
      <Row gutter={16}>
        <Col xs={24} md={12}><Form.Item name="name" label="ชื่อ SKU" extra="เว้นว่างได้ ระบบจะสร้างชื่อจากคุณลักษณะ"><Input size="large" placeholder="เช่น สติ๊กเกอร์ PVC 1.32 ม." /></Form.Item></Col>
        <Col xs={24} md={12}><Form.Item name="barcode" label="SKU / บาร์โค้ดหลัก" rules={[{ required: true, message: 'กรุณาระบุ SKU หรือบาร์โค้ด' }]}><Input size="large" prefix={<BarcodeOutlined />} placeholder="SKU-001" /></Form.Item></Col>
      </Row>
      <div className="pvm-section-heading compact"><AppstoreOutlined /><div><Text strong>คุณลักษณะ Variant</Text></div></div>
      <Row gutter={16}>
        {parseJson(selected?.attribute_schema, []).map(attribute => (
          <Col xs={24} md={12} key={attribute.key}>
            <Form.Item name={['variant_attributes', attribute.key]} label={attribute.label || attribute.key} rules={[{ required: true, message: `กรุณาระบุ${attribute.label || attribute.key}` }]}><Input size="large" /></Form.Item>
          </Col>
        ))}
      </Row>
      <Row gutter={16}>
        <Col xs={12} md={8}><Form.Item name="price" label="ราคาขาย" rules={[{ required: true }]}><InputNumber min={0} suffix="บาท" style={{ width: '100%' }} /></Form.Item></Col>
        <Col xs={12} md={8}><Form.Item name="cost" label="ต้นทุนมาตรฐาน"><InputNumber min={0} suffix="บาท" style={{ width: '100%' }} /></Form.Item></Col>
        <Col xs={12} md={8}><Form.Item name="min_stock" label="จุดสั่งซื้อ"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
      </Row>
      <Card size="small" className="pvm-switch-card">
        <Space size="large" wrap>
          <Form.Item name="track_inventory" valuePropName="checked" noStyle><Switch checkedChildren="ติดตามสต็อก" unCheckedChildren="ไม่ติดตามสต็อก" /></Form.Item>
          <Form.Item name="track_serial" valuePropName="checked" noStyle><Switch checkedChildren="ใช้ Serial" unCheckedChildren="ไม่ใช้ Serial" /></Form.Item>
        </Space>
      </Card>
    </Form>
  )

  const variantUnitEditor = (
    <Form form={variantForm} layout="vertical" className="pvm-form">
      <div className="pvm-info-note"><UnorderedListOutlined /><span>กำหนดหน่วยฐานก่อน แล้วเพิ่มหน่วยซื้อ/ขาย เช่น 1 แพ็ค = 12 ชิ้น</span></div>
      <Form.List name="units">{(fields, { add, remove }) => <>
        {fields.map((field, index) => (
          <Card key={field.key} size="small" className="pvm-unit-card" title={`หน่วยที่ ${index + 1}`} extra={index > 0 && <Button danger type="text" onClick={() => remove(field.name)}>นำออก</Button>}>
            <Row gutter={12}>
              <Col xs={24} md={6}><Form.Item {...field} name={[field.name, 'unit_name']} label="ชื่อหน่วย" rules={[{ required: true }]}><Input placeholder="ชิ้น / แพ็ค / ลัง" /></Form.Item></Col>
              <Col xs={12} md={4}><Form.Item {...field} name={[field.name, 'conversion_to_base']} label="เท่ากับหน่วยฐาน" rules={[{ required: true }]}><InputNumber min={0.000001} style={{ width: '100%' }} /></Form.Item></Col>
              <Col xs={12} md={6}><Form.Item {...field} name={[field.name, 'barcode']} label="บาร์โค้ดหน่วย"><Input /></Form.Item></Col>
              <Col xs={12} md={4}><Form.Item {...field} name={[field.name, 'sale_price']} label="ราคาขาย"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
              <Col xs={12} md={4}><Form.Item {...field} name={[field.name, 'purchase_price']} label="ราคาซื้อ"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
            </Row>
            <Space wrap>
              <Form.Item {...field} name={[field.name, 'is_base']} valuePropName="checked" noStyle><Switch checkedChildren="หน่วยฐาน" unCheckedChildren="หน่วยย่อย" /></Form.Item>
              <Form.Item {...field} name={[field.name, 'is_default_sale']} valuePropName="checked" noStyle><Switch checkedChildren="ขายเริ่มต้น" unCheckedChildren="หน่วยขาย" /></Form.Item>
              <Form.Item {...field} name={[field.name, 'is_default_purchase']} valuePropName="checked" noStyle><Switch checkedChildren="ซื้อเริ่มต้น" unCheckedChildren="หน่วยซื้อ" /></Form.Item>
            </Space>
          </Card>
        ))}
        <Button dashed block icon={<PlusOutlined />} onClick={() => add({ conversion_to_base: 1 })}>เพิ่มหน่วยบรรจุภัณฑ์</Button>
      </>}</Form.List>
    </Form>
  )

  const variantWarrantyEditor = (
    <Form form={variantForm} layout="vertical" className="pvm-form">
      <div className="pvm-section-heading"><SafetyCertificateOutlined /><div><Text strong>การรับประกันของ SKU นี้</Text><Text type="secondary">ระบบจะเก็บค่าเหล่านี้เป็น snapshot เมื่อยืนยันส่งมอบ</Text></div></div>
      <Row gutter={16}>
        <Col xs={12}><Form.Item name="shop_warranty_months" label="ประกันร้าน (เดือน)"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
        <Col xs={12}><Form.Item name="manufacturer_warranty_months" label="ประกันผู้ผลิต (เดือน)"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
      </Row>
      <Form.Item name="warranty_terms" label="เงื่อนไขรับประกัน"><Input.TextArea rows={5} placeholder="ระบุเงื่อนไข ข้อยกเว้น และช่องทางติดต่อ" /></Form.Item>
    </Form>
  )

  const unitEditor = (
    <Form form={unitForm} layout="vertical" className="pvm-form">
      <div className="pvm-info-note"><UnorderedListOutlined /><span>จำนวนสต็อกทั้งหมดอ้างอิงหน่วยฐาน การแก้ Factor จะมีผลกับเอกสารใหม่เท่านั้น</span></div>
      <Form.List name="units">{(fields, { add, remove }) => <>
        {fields.map((field, index) => (
          <Card size="small" key={field.key} className="pvm-unit-card" title={`${index === 0 ? 'หน่วยฐาน' : 'หน่วยย่อย'} · ${index + 1}`} extra={index > 0 && (
            <Popconfirm title="นำหน่วยนี้ออก?" onConfirm={() => remove(field.name)}><Button danger type="text">นำออก</Button></Popconfirm>
          )}>
            <Row gutter={12}>
              <Col xs={24} md={6}><Form.Item {...field} name={[field.name, 'unit_name']} label="ชื่อหน่วย" rules={[{ required: true }]}><Input placeholder="ชิ้น / แพ็ค / ลัง" /></Form.Item></Col>
              <Col xs={12} md={4}><Form.Item {...field} name={[field.name, 'conversion_to_base']} label="เท่ากับหน่วยฐาน" rules={[{ required: true }]}><InputNumber min={0.000001} style={{ width: '100%' }} /></Form.Item></Col>
              <Col xs={12} md={6}><Form.Item {...field} name={[field.name, 'barcode']} label="บาร์โค้ดหน่วย"><Input /></Form.Item></Col>
              <Col xs={12} md={4}><Form.Item {...field} name={[field.name, 'sale_price']} label="ราคาขาย"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
              <Col xs={12} md={4}><Form.Item {...field} name={[field.name, 'purchase_price']} label="ราคาซื้อ"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
            </Row>
            <Space wrap>
              <Form.Item {...field} name={[field.name, 'is_base']} valuePropName="checked" noStyle><Switch checkedChildren="หน่วยฐาน" unCheckedChildren="หน่วยย่อย" /></Form.Item>
              <Form.Item {...field} name={[field.name, 'is_default_sale']} valuePropName="checked" noStyle><Switch checkedChildren="ขายเริ่มต้น" unCheckedChildren="หน่วยขาย" /></Form.Item>
              <Form.Item {...field} name={[field.name, 'is_default_purchase']} valuePropName="checked" noStyle><Switch checkedChildren="ซื้อเริ่มต้น" unCheckedChildren="หน่วยซื้อ" /></Form.Item>
            </Space>
          </Card>
        ))}
        <Button block dashed icon={<PlusOutlined />} onClick={() => add({ conversion_to_base: 1 })}>เพิ่มหน่วยบรรจุภัณฑ์</Button>
      </>}</Form.List>
      <div className="pvm-inline-actions"><Button type="primary" onClick={saveUnits}>บันทึกหน่วยและราคา</Button></div>
    </Form>
  )

  const skuEditor = (
    <Form form={skuForm} layout="vertical" className="pvm-form">
      <Row gutter={16}>
        <Col xs={24} md={12}><Form.Item name="name" label="ชื่อ SKU" rules={[{ required: true }]}><Input size="large" /></Form.Item></Col>
        <Col xs={24} md={12}><Form.Item name="barcode" label="SKU / บาร์โค้ดหลัก" rules={[{ required: true }]}><Input size="large" prefix={<BarcodeOutlined />} /></Form.Item></Col>
        <Col xs={12} md={8}><Form.Item name="price" label="ราคาขาย"><InputNumber min={0} suffix="บาท" style={{ width: '100%' }} /></Form.Item></Col>
        <Col xs={12} md={8}><Form.Item name="cost" label="ต้นทุนมาตรฐาน"><InputNumber min={0} suffix="บาท" style={{ width: '100%' }} /></Form.Item></Col>
        <Col xs={12} md={8}><Form.Item name="min_stock" label="จุดสั่งซื้อ"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
      </Row>
      <Card size="small" className="pvm-switch-card">
        <Space size="large" wrap>
          <Form.Item name="track_inventory" valuePropName="checked" noStyle><Switch checkedChildren="ติดตามสต็อก" unCheckedChildren="ไม่ติดตามสต็อก" /></Form.Item>
          <Form.Item name="track_serial" valuePropName="checked" noStyle><Switch disabled={Number(detail?.stock) > 0 && !detail?.track_serial} checkedChildren="ใช้ Serial" unCheckedChildren="ไม่ใช้ Serial" /></Form.Item>
        </Space>
      </Card>
      <Divider orientation="left">การรับประกัน</Divider>
      <Row gutter={16}>
        <Col xs={12} md={6}><Form.Item name="shop_warranty_months" label="ประกันร้าน (เดือน)"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
        <Col xs={12} md={6}><Form.Item name="manufacturer_warranty_months" label="ประกันผู้ผลิต (เดือน)"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
        <Col xs={12} md={6}><Form.Item name="inventory_account_id" label="บัญชีสินค้าคงเหลือ"><InputNumber min={1} style={{ width: '100%' }} /></Form.Item></Col>
        <Col xs={12} md={6}><Form.Item name="cogs_account_id" label="บัญชีต้นทุนขาย"><InputNumber min={1} style={{ width: '100%' }} /></Form.Item></Col>
      </Row>
      <Form.Item name="warranty_terms" label="เงื่อนไขรับประกัน"><Input.TextArea rows={3} /></Form.Item>
      <div className="pvm-inline-actions"><Button type="primary" onClick={saveSku}>บันทึกข้อมูล SKU</Button></div>
    </Form>
  )

  const recipeEditor = (
    <Form form={recipeForm} layout="vertical" className="pvm-form">
      <div className="pvm-info-note"><BoxPlotOutlined /><span>BOM แยกตาม SKU การบันทึกจะสร้าง revision ใหม่โดยไม่เปลี่ยนต้นทุนเอกสารย้อนหลัง</span></div>
      <Form.List name="components">{(fields, { add, remove }) => <>
        {fields.map((field, index) => (
          <Card size="small" key={field.key} className="pvm-unit-card" title={`องค์ประกอบ ${index + 1}`} extra={<Button danger type="text" onClick={() => remove(field.name)}>ลบ</Button>}>
            <Row gutter={10}>
              <Col xs={12} md={5}><Form.Item {...field} name={[field.name, 'component_type']} label="ประเภท" rules={[{ required: true }]}><Select options={[
                ['roll_material', 'วัสดุม้วน'], ['general_material', 'วัสดุทั่วไป'], ['ink', 'หมึก'], ['labor', 'ค่าแรง'], ['electricity', 'ค่าไฟ'], ['fixed', 'ต้นทุนคงที่']
              ].map(([value, label]) => ({ value, label }))} /></Form.Item></Col>
              <Col xs={24} md={7}><Form.Item {...field} name={[field.name, 'material_selection']} label="วัสดุ / หน่วยที่ใช้"><Select allowClear showSearch optionFilterProp="label" options={componentOptions} /></Form.Item></Col>
              <Col xs={12} md={4}><Form.Item {...field} name={[field.name, 'calculation_method']} label="วิธีคิด" rules={[{ required: true }]}><Select options={[
                ['per_sqm', 'ต่อ ตร.ม.'], ['per_linear_m', 'ต่อเมตร'], ['per_piece', 'ต่อชิ้น'], ['per_hour', 'ต่อชั่วโมง'], ['fixed_per_bill', 'คงที่/บิล']
              ].map(([value, label]) => ({ value, label }))} /></Form.Item></Col>
              <Col xs={8} md={3}><Form.Item {...field} name={[field.name, 'rate']} label="อัตรา"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
              <Col xs={8} md={3}><Form.Item {...field} name={[field.name, 'quantity_factor']} label="ตัวคูณ"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item></Col>
            </Row>
            <Space wrap>
              <Form.Item {...field} name={[field.name, 'track_stock']} valuePropName="checked" noStyle><Switch checkedChildren="ตัดสต็อก" unCheckedChildren="วิเคราะห์" /></Form.Item>
              <Form.Item {...field} name={[field.name, 'allow_rotation']} valuePropName="checked" noStyle><Switch checkedChildren="หมุนงานได้" unCheckedChildren="ไม่หมุน" /></Form.Item>
              <Form.Item {...field} name={[field.name, 'waste_percent']} label="สูญเสีย %" style={{ margin: 0 }}><InputNumber min={0} style={{ width: 110 }} /></Form.Item>
            </Space>
          </Card>
        ))}
        <Button dashed block icon={<PlusOutlined />} onClick={() => add({ component_type: 'general_material', calculation_method: 'per_piece', quantity_factor: 1, rate: 0, waste_percent: 0, track_stock: true, allow_rotation: true })}>เพิ่มองค์ประกอบ BOM</Button>
      </>}</Form.List>
      <Form.Item name="notes" label="หมายเหตุ revision" style={{ marginTop: 16 }}><Input.TextArea rows={2} /></Form.Item>
      <div className="pvm-inline-actions"><Button type="primary" onClick={saveRecipe}>บันทึกเป็น BOM revision ใหม่</Button></div>
    </Form>
  )

  const lotsTable = <Table size="small" rowKey="id" dataSource={detailData.lots} pagination={false} scroll={{ x: 720 }} columns={[
    { title: 'ล็อต', dataIndex: 'lot_number' }, { title: 'Batch', dataIndex: 'batch_number' }, { title: 'รับเข้า', dataIndex: 'received_quantity_base' }, { title: 'คงเหลือ', dataIndex: 'remaining_quantity_base' }, { title: 'จอง', dataIndex: 'reserved_quantity_base' }, { title: 'ต้นทุน/ฐาน', dataIndex: 'unit_cost_base', render: money }, { title: 'สถานะ', dataIndex: 'status' }
  ]} />

  const serialTable = <Table size="small" rowKey="id" dataSource={detailData.serials} scroll={{ x: 760 }} columns={[
    { title: 'Serial', dataIndex: 'serial_number' }, { title: 'สถานะ', dataIndex: 'status', render: value => <Tag>{value}</Tag> }, { title: 'ล็อต', dataIndex: 'lot_number' }, { title: 'ต้นทุน', dataIndex: 'unit_cost', render: money }, { title: 'สิ้นสุดประกันร้าน', dataIndex: 'shop_warranty_end_date' }
  ]} />

  const movementsTable = <Table size="small" rowKey="id" dataSource={detailData.movements} scroll={{ x: 820 }} columns={[
    { title: 'เวลา', dataIndex: 'created_at' }, { title: 'ประเภท', dataIndex: 'movement_type' }, { title: 'จำนวนฐาน', dataIndex: 'quantity_base' }, { title: 'มูลค่า', dataIndex: 'value_amount', render: money }, { title: 'อ้างอิง', render: (_, row) => `${row.reference_type || '-'} #${row.reference_id || '-'}` }, { title: 'เหตุผล', dataIndex: 'reason' }
  ]} />

  const detailTabs = detail ? [
    { key: 'overview', label: 'ข้อมูล SKU / ประกัน', children: skuEditor },
    { key: 'units', label: <><UnorderedListOutlined /> หน่วยและราคา</>, children: unitEditor },
    { key: 'bom', label: <><BoxPlotOutlined /> BOM</>, children: recipeEditor },
    { key: 'lots', label: <><StockOutlined /> ล็อต</>, children: lotsTable },
    { key: 'serials', label: <><BarcodeOutlined /> Serial</>, children: serialTable },
    { key: 'movements', label: 'Stock Ledger', children: movementsTable }
  ] : []

  const modalTitle = workspace === 'family'
    ? 'เพิ่มสินค้าแม่'
    : workspace === 'variant'
      ? `เพิ่ม Variant · ${selected?.name || ''}`
      : detail?.name || 'จัดการสินค้า'

  return (
    <div className="pvm-workspace">
      <div className="pvm-toolbar">
        <div>
          <Title level={4}>สินค้าแม่และ Variant</Title>
          <Text type="secondary">เลือกสินค้าแม่ทางซ้าย แล้วจัดการทุก SKU หน่วย ราคา BOM และประกันใน Workspace เดียว</Text>
        </div>
        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={() => { loadFamilies(); loadVariants() }}>รีเฟรช</Button>
          <Button icon={<PlusOutlined />} onClick={openFamily}>เพิ่มสินค้าแม่</Button>
          <Button type="primary" icon={<PlusOutlined />} disabled={!selected} onClick={openVariant}>เพิ่ม Variant</Button>
        </Space>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={7} xl={6}>
          <Card className="pvm-family-card" title="สินค้าแม่" bordered={false}>
            {isMobile ? (
              <Select
                size="large"
                style={{ width: '100%' }}
                value={selectedId}
                onChange={setSelectedId}
                placeholder="เลือกสินค้าแม่"
                options={families.map(item => ({ value: item.id, label: `${item.name} (${item.variant_count || 0})` }))}
              />
            ) : (
              <>
                <Input prefix={<SearchOutlined />} allowClear value={familySearch} onChange={event => setFamilySearch(event.target.value)} placeholder="ค้นหาสินค้าแม่" className="pvm-family-search" />
                <List
                  loading={loading}
                  dataSource={filteredFamilies}
                  locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="ยังไม่มีสินค้าแม่" /> }}
                  renderItem={item => (
                    <List.Item className={`pvm-family-item ${Number(item.id) === Number(selectedId) ? 'active' : ''}`} onClick={() => setSelectedId(item.id)}>
                      <List.Item.Meta
                        avatar={<Avatar shape="square" icon={<AppstoreOutlined />} />}
                        title={<Text strong>{item.name}</Text>}
                        description={<><Text type="secondary">{item.brand || 'ไม่ระบุแบรนด์'}</Text><Tag>{item.variant_count || 0} Variant</Tag></>}
                      />
                    </List.Item>
                  )}
                />
              </>
            )}
          </Card>
        </Col>

        <Col xs={24} md={17} xl={18}>
          <Card className="pvm-variant-card" bordered={false}>
            {selected ? (
              <>
                <div className="pvm-selected-header">
                  <div><Title level={4}>{selected.name}</Title><Space wrap>{selected.brand && <Tag color="blue">{selected.brand}</Tag>}<Text type="secondary">{selected.description || 'ไม่มีรายละเอียด'}</Text></Space></div>
                  <div className="pvm-mini-stats"><Statistic title="Variant" value={variants.length} /><Statistic title="สต็อกรวม" value={variants.reduce((sum, item) => sum + Number(item.stock || 0), 0)} /></div>
                </div>
                {isMobile ? (
                  <List
                    loading={loading}
                    dataSource={variants}
                    locale={{ emptyText: <Empty description="ยังไม่มี Variant" /> }}
                    renderItem={item => (
                      <List.Item className="pvm-mobile-sku" onClick={() => openDetail(item)}>
                        <div className="pvm-mobile-sku-top"><Space><Avatar shape="square" icon={<BarcodeOutlined />} /><div><Text strong>{item.name}</Text><br /><Text type="secondary">{item.barcode || '-'}</Text></div></Space><SettingOutlined /></div>
                        <div>{renderAttributes(item.variant_attributes)}</div>
                        <Row gutter={8} className="pvm-mobile-sku-stats">
                          <Col span={8}><Text type="secondary">คงเหลือ</Text><Text strong>{Number(item.stock || 0).toLocaleString()} {item.unit}</Text></Col>
                          <Col span={8}><Text type="secondary">ราคาขาย</Text><Text strong>{money(item.price)}</Text></Col>
                          <Col span={8}><Text type="secondary">ต้นทุน</Text><Text strong>{money(item.cost)}</Text></Col>
                        </Row>
                      </List.Item>
                    )}
                  />
                ) : (
                  <Table rowKey="id" loading={loading} dataSource={variants} columns={columns} scroll={{ x: 1050 }} pagination={{ pageSize: 20 }} />
                )}
              </>
            ) : (
              <Empty description="เริ่มต้นโดยสร้างหรือเลือกสินค้าแม่"><Button type="primary" icon={<PlusOutlined />} onClick={openFamily}>สร้างสินค้าแม่รายการแรก</Button></Empty>
            )}
          </Card>
        </Col>
      </Row>

      <Modal
        className="pvm-modal"
        title={modalTitle}
        open={Boolean(workspace)}
        onCancel={closeWorkspace}
        width={workspace === 'family' ? 780 : 1120}
        footer={workspace === 'family' ? [
          <Button key="cancel" onClick={closeWorkspace}>ยกเลิก</Button>,
          <Button key="save" type="primary" onClick={saveFamily}>สร้างสินค้าแม่</Button>
        ] : workspace === 'variant' ? [
          <Button key="cancel" onClick={closeWorkspace}>ยกเลิก</Button>,
          <Button key="save" type="primary" onClick={saveVariant}>บันทึก Variant</Button>
        ] : null}
        destroyOnClose={false}
        centered={!isMobile}
      >
        {workspace === 'family' && familyEditor}
        {workspace === 'variant' && (
          <Tabs className="pvm-editor-tabs" items={[
            { key: 'basic', label: '1. ข้อมูล SKU', children: variantBasicEditor },
            { key: 'units', label: '2. หน่วยและราคา', children: variantUnitEditor },
            { key: 'warranty', label: '3. การรับประกัน', children: variantWarrantyEditor }
          ]} />
        )}
        {workspace === 'detail' && detail && (
          <>
            <div className="pvm-detail-summary">
              <Space><Avatar size={48} shape="square" icon={<BarcodeOutlined />} /><div><Text type="secondary">SKU / บาร์โค้ด</Text><Text strong>{detail.barcode || '-'}</Text></div></Space>
              <div><Text type="secondary">คงเหลือ</Text><Text strong>{Number(detail.stock || 0).toLocaleString()} {detail.unit}</Text></div>
              <div><Text type="secondary">ราคาขาย</Text><Text strong>{money(detail.price)} บาท</Text></div>
              <div><Text type="secondary">ต้นทุน</Text><Text strong>{money(detail.cost)} บาท</Text></div>
            </div>
            <Tabs className="pvm-editor-tabs" items={detailTabs} />
          </>
        )}
      </Modal>
    </div>
  )
}

export default ProductVariantManager
